import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import { formatPriceAmount } from "../utils/saleUtils";
import "./StripeCheckoutModal.css";

const stagingStripeKey = 'pk_test_51PFBb8RqldiTE8bjsee3CEN27FqrzLCtmRrJoCpzS9dZ8kiU2JMJZzJLManEn3ctsBRZ3xQ00Q9XLPMXbsDKt05b00fbfyRqK1';
const productionStripeKey = 'pk_live_51PFBb8RqldiTE8bjdgL6VeuAfYejfRXMqCB5SeS2IKeWdyYYViJ9195uNTrsFGeGOHAs3DPYLgcim5eRIQobOLjI00OwgzJaw3';

const stripePromise = loadStripe(
  productionStripeKey
);

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#32325d",
      fontFamily: "Arial, sans-serif",
      "::placeholder": {
        color: "#a0aec0",
      },
    },
    invalid: {
      color: "#e53e3e",
    },
  },
};

const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

const centsToAmount = (cents) => {
  if (cents == null || cents === "") return null;
  const value = Number(cents);
  if (Number.isNaN(value)) return null;
  return roundMoney(value / 100);
};

const readApiMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.message) return String(data.message);
  if (data.detail) return String(data.detail);
  if (data.error) return String(data.error);
  if (Array.isArray(data.code)) return data.code.filter(Boolean).join(" ");
  if (typeof data.code === "string") return data.code;
  return fallback;
};

function CheckoutForm({ onClose, items, cartItems, fetchcart, open, subtotal, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [cardholderName, setCardholderName] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [quote, setQuote] = useState(null);
  const navigate = useNavigate();
  const { profiledata } = useIndexPageData();

  const numericSubtotal = Number(subtotal);
  const hasSubtotal = Number.isFinite(numericSubtotal) && numericSubtotal > 0;

  useEffect(() => {
    if (!open) return;
    setLoading(false);
    setErrorMessage(null);
    setCardholderName("");
    setTermsAndConditions(false);
    setCouponInput("");
    setCouponLoading(false);
    setCouponError("");
    setAppliedCoupon(null);
    setQuote(null);
  }, [open]);

  const preview = useMemo(() => {
    if (quote?.charge != null) return quote;
    if (!hasSubtotal) return null;
    const percentage = Number(appliedCoupon?.discount_percentage) || 0;
    const discount = appliedCoupon
      ? roundMoney(numericSubtotal * (percentage / 100))
      : 0;
    return {
      original: numericSubtotal,
      discount,
      charge: roundMoney(Math.max(0, numericSubtotal - discount)),
      percentage,
      preview: true,
    };
  }, [appliedCoupon, hasSubtotal, numericSubtotal, quote]);

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) {
      setCouponError("Enter a coupon code");
      return;
    }

    setCouponLoading(true);
    setCouponError("");
    setErrorMessage(null);

    try {
      const result = await apiClient.post("/coupons/verify/", { code });
      const data = result.data || {};
      if (!result.ok || data.valid === false) {
        setAppliedCoupon(null);
        setQuote(null);
        setCouponError(readApiMessage(data, "This coupon is not valid"));
        return;
      }

      const percentage = Number(data.discount_percentage);
      setAppliedCoupon({
        code: data.code || code,
        discount_percentage: Number.isFinite(percentage) ? percentage : 0,
      });
      setQuote(null);
      setCouponInput(data.code || code);
    } catch {
      setAppliedCoupon(null);
      setCouponError("Unable to verify this coupon. Please try again.");
    } finally {
      setCouponLoading(false);
    }
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
    setQuote(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage(null);

    try {
      const payload = { items };
      if (appliedCoupon?.code) {
        payload.coupon_code = appliedCoupon.code;
      }

      const res = await apiClient.post("/checkout/", payload);
      if (!res.ok) {
        setErrorMessage(readApiMessage(res.data, "Failed to start payment"));
        setLoading(false);
        return;
      }

      const data = res.data || {};
      const original = centsToAmount(data.original_amount_cents);
      const discount = centsToAmount(data.discount_cents);
      const charge = centsToAmount(data.charge_amount_cents);
      if (charge != null) {
        setQuote({
          original: original ?? charge,
          discount: discount ?? 0,
          charge,
          percentage: Number(data.coupon?.discount_percentage) || 0,
          preview: false,
        });
      }
      if (data.coupon?.code) {
        setAppliedCoupon({
          code: data.coupon.code,
          discount_percentage: Number(data.coupon.discount_percentage) || 0,
        });
      }

      const paymentInt = data.paymentIntent;

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        paymentInt?.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              name: cardholderName,
              email: profiledata?.email,
            },
          },
        }
      );

      if (error) {
        setErrorMessage(error.message);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        await apiClient.post("/stripeSuccess/", {
          payment_intent_id: paymentIntent.id,
        });
        if (cartItems && cartItems?.length) {
          await Promise.all(
            cartItems?.map(async (item) => {
              const result = await apiClient.delete(`/cartitems/${item?.id}/`);
              if (!result.ok) handleErrors(result?.data);
            })
          );
          await fetchcart();
        }

        if (onSuccess) {
          // The caller continues (e.g. the editor adds the bought mockup).
          setLoading(false);
          onSuccess(paymentIntent);
          return;
        }
        onClose();
        navigate("/user/dashboard");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Unexpected error occurred");
    }

    setLoading(false);
  };

  const payLabel = loading
    ? "Processing..."
    : preview?.charge != null
      ? `Pay ${formatPriceAmount(preview.charge)}`
      : "Pay";

  return (
    <form onSubmit={handleSubmit} className="mt-3">
      <div className="mb-3">
        <Form.Label>Email</Form.Label>
        <div className="border rounded p-2 bg-light">
          {profiledata?.email || "No email available"}
        </div>
      </div>

      <Form.Group className="mb-3">
        <Form.Label>Cardholder name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Full name on card"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Card information</Form.Label>
        <div className="border rounded">
          <div className="p-3 border-bottom d-flex align-items-center justify-between">
            <CardNumberElement
              options={cardElementOptions}
              className="flex-grow-1"
            />

            <div className="d-flex ms-3 gap-2" style={{ width: "120px" }}>
              <img
                src="../assets/images/visa.svg"
                alt="Visa"
                style={{ width: "30px" }}
              />
              <img
                src="../assets/images/mastercard.png"
                alt="MasterCard"
                style={{ width: "30px" }}
              />
              <img
                src="../assets/images/unionpay.png"
                alt="UnionPay"
                style={{ width: "30px" }}
              />
            </div>
          </div>

          <Row className="g-0">
            <Col xs={6}>
              <div className="p-3 border-end">
                <CardExpiryElement options={cardElementOptions} />
              </div>
            </Col>
            <Col xs={6}>
              <div className="p-3">
                <CardCvcElement options={cardElementOptions} />
              </div>
            </Col>
          </Row>
        </div>
      </Form.Group>

      <div className="stripe-checkout-coupon mb-3">
        <Form.Label className="mb-2">Coupon code</Form.Label>
        <div className="stripe-checkout-coupon__row">
          <Form.Control
            type="text"
            placeholder="Enter code"
            value={couponInput}
            onChange={(e) => {
              setCouponInput(e.target.value.toUpperCase());
              setCouponError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!appliedCoupon && !couponLoading && !loading) {
                  handleApplyCoupon();
                }
              }
            }}
            disabled={Boolean(appliedCoupon) || couponLoading || loading}
            autoComplete="off"
          />
          <button
            type="button"
            className="stripe-checkout-coupon__apply"
            onClick={handleApplyCoupon}
            disabled={Boolean(appliedCoupon) || couponLoading || loading}
          >
            {couponLoading ? "Checking..." : "Apply"}
          </button>
        </div>
        {couponError ? (
          <p className="text-danger small mb-0 mt-2">{couponError}</p>
        ) : null}
        {appliedCoupon ? (
          <div className="stripe-checkout-coupon__applied">
            <span>
              {appliedCoupon.code} applied · {appliedCoupon.discount_percentage}% off
            </span>
            <button
              type="button"
              className="stripe-checkout-coupon__remove"
              onClick={clearCoupon}
              disabled={loading}
            >
              Remove
            </button>
          </div>
        ) : (
          <p className="small text-muted mb-0 mt-2">
            Optional. Discount is confirmed when you pay.
          </p>
        )}
      </div>

      {preview ? (
        <div className="stripe-checkout-summary mb-3">
          <div className="stripe-checkout-summary__row">
            <span>Subtotal</span>
            <span>{formatPriceAmount(preview.original)}</span>
          </div>
          {appliedCoupon ? (
            <div className="stripe-checkout-summary__row stripe-checkout-summary__save">
              <span>
                Discount ({appliedCoupon.code}
                {appliedCoupon.discount_percentage
                  ? ` · ${appliedCoupon.discount_percentage}%`
                  : ""}
                )
              </span>
              <span>-{formatPriceAmount(preview.discount)}</span>
            </div>
          ) : null}
          <div className="stripe-checkout-summary__row stripe-checkout-summary__row--total">
            <span>Total</span>
            <span>{formatPriceAmount(preview.charge)}</span>
          </div>
        </div>
      ) : null}

      {errorMessage && (
        <div className="alert alert-danger small" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="form-group form-check mb-3 align-items-start justify-content-start d-flex gap-1">
        <input
          required
          type="checkbox"
          className="form-check-input mt-1"
          id="paymentTermsCheck"
          style={{ borderWidth: "2px" }}
          checked={termsAndConditions}
          onChange={(e) => setTermsAndConditions(e.target.checked)}
        />
        <label
          className="form-check-label text-start"
          htmlFor="paymentTermsCheck"
          style={{ fontSize: "11px", color: "#6c6c6c" }}
        >
          I declare that I have read the{" "}
          <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
            Terms And Conditions
          </Link>{" "}
          and the{" "}
          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>{" "}
          and accept their provisions
        </label>
      </div>

      <Button
        className="w-100 mt-3 stripe-checkout-pay"
        type="submit"
        size="lg"
        disabled={!stripe || loading || !termsAndConditions}
      >
        {payLabel}
      </Button>
      <div className="text-center mt-3 small text-muted">
        Powered by <strong>Stripe</strong> |{" "}
        <a
          href="https://stripe.com/legal/consumer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
        >
          Terms
        </a>{" "}
        |{" "}
        <a
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
        >
          Privacy
        </a>
      </div>
    </form>
  );
}

export default function StripeCheckoutModal({
  open,
  onClose,
  items,
  cartItems,
  fetchcart,
  subtotal,
  onSuccess,
}) {
  return (
    <Elements stripe={stripePromise}>
      <Modal show={open} onHide={onClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Complete Your Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CheckoutForm
            open={open}
            onClose={onClose}
            items={items}
            cartItems={cartItems}
            fetchcart={fetchcart}
            subtotal={subtotal}
            onSuccess={onSuccess}
          />
        </Modal.Body>
      </Modal>
    </Elements>
  );
}
